/**
 * Mi cuenta — `GET /api/v1/me` en palabras, instalar la aplicación (`OD-0065`)
 * y cerrar sesión.
 *
 * Los permisos se enseñan con su nombre en español (`i18n/es.ts`), no con el
 * código de `URS §9.1`. Un código que la aplicación no conoce se muestra tal
 * cual en vez de esconderse: ocultar un permiso es peor que mostrarlo feo.
 *
 * Arriba va lo que el usuario reconoce -su empresa y su plan-, y los
 * identificadores internos (`org-…`, `usr-…`) plegados en «Datos para
 * soporte». Antes eran lo PRIMERO que se leía, como si fueran sus datos
 * (auditoría de diseño, 2026-10-19). No se esconden: soporte los necesita.
 */
import { useState } from "react";
import { api, type Sesion } from "../api/cliente";
import { t } from "../i18n/es";
import { Boton, Tarjeta } from "../ui/primitivas";
import { InstalarAplicacion } from "./InstalarAplicacion";
import { SelectorDeEmpresa } from "./SelectorDeEmpresa";

export function PantallaCuenta({
  sesion,
  empresa = null,
  plan = null,
  onSalir,
  onCambioDeEmpresa,
}: {
  sesion: Sesion;
  /** Razón social del emisor, si ya está; `null` si no se sabe. */
  empresa?: string | null;
  /** Nombre visible del plan, si se sabe. */
  plan?: string | null;
  onSalir: () => void;
  /** OD-0060 D-04. En el móvil no hay lateral: el selector está también aquí. */
  onCambioDeEmpresa: () => void;
}) {
  const [saliendo, setSaliendo] = useState(false);

  async function salir(): Promise<void> {
    setSaliendo(true);
    // Aunque falle la red se vuelve a la entrada: quedarse en una pantalla de
    // sesión que el usuario ha pedido cerrar es peor. La cookie caduca sola.
    await api.logout();
    setSaliendo(false);
    onSalir();
  }

  return (
    <Tarjeta titulo={t.cuenta.titulo}>
      {empresa !== null || plan !== null ? (
        <dl className="datos">
          {empresa !== null ? (
            <>
              <dt>{t.cuenta.empresa}</dt>
              <dd>{empresa}</dd>
            </>
          ) : null}
          {plan !== null ? (
            <>
              <dt>{t.cuenta.plan}</dt>
              <dd>{plan}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
      <SelectorDeEmpresa id="cuenta" onCambiada={onCambioDeEmpresa} onSesionCaducada={onSalir} />
      <section aria-labelledby="titulo-permisos">
        <h3 id="titulo-permisos" className="subtitulo">
          {t.cuenta.permisos}
        </h3>
        {sesion.capabilities.length === 0 ? (
          <p>{t.cuenta.sinPermisos}</p>
        ) : (
          <ul className="lista-capacidades">
            {sesion.capabilities.map((c) => (
              <li key={c}>{t.capacidades[c] ?? c}</li>
            ))}
          </ul>
        )}
      </section>
      <InstalarAplicacion titulo={t.cuenta.instalar.titulo} />
      <details className="datos-soporte">
        <summary>{t.cuenta.soporte}</summary>
        <p className="ayuda-neutra">{t.cuenta.soporteDetalle}</p>
        <dl className="datos">
          <dt>{t.cuenta.organizacion}</dt>
          <dd>
            <code>{sesion.organizationId}</code>
          </dd>
          <dt>{t.cuenta.usuario}</dt>
          <dd>
            <code>{sesion.userId}</code>
          </dd>
        </dl>
      </details>
      <Boton variante="secundario" onClick={() => void salir()} deshabilitado={saliendo}>
        {saliendo ? t.cuenta.saliendo : t.cuenta.salir}
      </Boton>
    </Tarjeta>
  );
}

